import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Play } from "lucide-react";

const ALBUM_ART =
    "https://customer-assets-cm19k8pv.emergentagent.net/job_bejnni-artist/artifacts/mh1gvuyv_B790DFCB-4F0B-4A07-8B0E-4AD3D8AC60A6.png";
const SINGLE_ART =
    "https://customer-assets-cm19k8pv.emergentagent.net/job_bejnni-artist/artifacts/w9wohi7z_B348D4E2-AC04-424D-98B4-3B8B7858F4C8.jpeg";

const Equalizer = () => (
    <div className="eq" data-testid="track-equalizer" aria-hidden="true">
        <span />
        <span />
        <span />
    </div>
);

export default function MusicSection({
    tracks,
    currentIndex,
    isPlaying,
    onSelect,
}) {
    return (
        <section
            id="listen"
            data-testid="music-section"
            className="relative px-6 py-24 md:px-16 md:py-40"
        >
            <div className="absolute right-0 top-0 h-[40vh] w-[30vw] rounded-full bg-white/[0.03] blur-[100px]" />
            <div className="mb-16 flex flex-col gap-4 md:mb-24 md:flex-row md:items-end md:justify-between">
                <div>
                    <p className="mb-4 font-mono text-xs uppercase tracking-[0.3em] text-zinc-500">
                        01 / Listen
                    </p>
                    <h2 className="font-serif text-4xl leading-none tracking-tight sm:text-5xl lg:text-6xl">
                        Latest <span className="italic text-zinc-400">release</span>
                    </h2>
                </div>
                <p className="max-w-xs text-sm leading-relaxed text-zinc-500">
                    Five tracks from the record. Press play — the player
                    follows you down the page.
                </p>
            </div>

            <div className="grid grid-cols-1 gap-16 md:grid-cols-12 md:gap-12">
                <div className="md:col-span-5">
                    <div className="md:sticky md:top-24">
                        <motion.div
                            initial={{ opacity: 0, scale: 0.95 }}
                            whileInView={{ opacity: 1, scale: 1 }}
                            viewport={{ once: true, margin: "-100px" }}
                            transition={{ duration: 1.2, ease: [0.16, 1, 0.3, 1] }}
                            className="group overflow-hidden"
                        >
                            <img
                                src={ALBUM_ART}
                                alt="NO LIGHT. NO SOUND. ONLY WHAT REMAINS. album artwork"
                                data-testid="album-artwork"
                                className="aspect-square w-full object-cover transition-transform duration-700 group-hover:scale-105"
                            />
                        </motion.div>
                        <p
                            data-testid="album-caption"
                            className="mt-4 font-mono text-[11px] uppercase tracking-[0.25em] text-zinc-500"
                        >
                            No light. No sound. Only what remains. — LP
                        </p>
                        <div className="mt-10 flex items-center gap-6">
                            <motion.img
                                initial={{ opacity: 0, scale: 0.95 }}
                                whileInView={{ opacity: 1, scale: 1 }}
                                viewport={{ once: true }}
                                transition={{ duration: 1, ease: [0.16, 1, 0.3, 1] }}
                                src={SINGLE_ART}
                                alt="i bloom better when i'm dead. single artwork"
                                data-testid="single-artwork"
                                className="h-28 w-28 object-cover"
                            />
                            <p className="max-w-[180px] font-mono text-[11px] uppercase tracking-[0.25em] text-zinc-500">
                                i bloom better when i'm dead. — single
                            </p>
                        </div>
                    </div>
                </div>

                <div className="border-t border-white/10 md:col-span-7">
                    {tracks.map((track, i) => {
                        const active = i === currentIndex;
                        return (
                            <motion.div
                                key={track.title}
                                data-testid={`track-item-${i}`}
                                onClick={() => onSelect(i)}
                                role="button"
                                tabIndex={0}
                                onKeyDown={(e) => e.key === "Enter" && onSelect(i)}
                                initial={{ opacity: 0, y: 30 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true, margin: "-80px" }}
                                transition={{
                                    duration: 0.8,
                                    delay: i * 0.12,
                                    ease: [0.16, 1, 0.3, 1],
                                }}
                                className="group flex w-full cursor-pointer items-center gap-6 border-b border-white/10 py-6 text-left transition-colors duration-500 hover:bg-white/5 md:gap-8 md:py-8"
                                aria-label={`Play ${track.title}`}
                            >
                                <span className="w-10 font-mono text-xs text-zinc-500 transition-colors group-hover:text-bone">
                                    0{i + 1}
                                </span>
                                {track.artwork && (
                                    <img
                                        src={track.artwork}
                                        alt=""
                                        className="h-10 w-10 object-cover"
                                    />
                                )}
                                <span
                                    className={`flex-1 font-serif text-2xl tracking-tight transition-colors duration-300 md:text-3xl ${
                                        active
                                            ? "text-bone"
                                            : "text-zinc-400 group-hover:text-bone"
                                    }`}
                                >
                                    {track.title}
                                </span>
                                {track.lyrics && (
                                    <Link
                                        to={track.lyrics}
                                        data-testid={`lyrics-link-${i}`}
                                        onClick={(e) => e.stopPropagation()}
                                        className="hidden font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-600 underline-offset-4 transition-colors duration-300 hover:text-blood hover:underline md:block"
                                    >
                                        Lyrics
                                    </Link>
                                )}
                                {active && isPlaying ? (
                                    <Equalizer />
                                ) : (
                                    <Play
                                        className={`h-4 w-4 transition-all duration-300 ${
                                            active
                                                ? "text-blood"
                                                : "text-zinc-600 group-hover:text-bone"
                                        }`}
                                    />
                                )}
                                <span className="font-mono text-xs text-zinc-500">
                                    {track.length}
                                </span>
                            </motion.div>
                        );
                    })}
                </div>
            </div>
        </section>
    );
}
